import {
  FLEET,
  MEGASTRUCTURE,
  TRADE_AGREEMENT,
  TRADE_GOOD_IDS,
  type ProjectId,
  type TechId,
} from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import { PIRATES, outpostYield, type GameEvent, type GameState, type Hex } from '@sd/sim';
import type { Viewer } from '../map/describe';
import { clock, num, outpostName, playerName, shipName } from './format';
import { buildingDoes, reasonText } from './planetModel';
import { techEffects, techUnlocks } from './researchModel';

/**
 * What a match event means to the player (#56): a notification for the
 * stack at the map's edge and the log, or nothing. Pure, so it is tested
 * without a browser. Warm notes are the star's (crises, instability,
 * projects); alerts are threats from players and pirates; good news is the
 * player's own progress.
 */
export type NoteKind = 'heat' | 'alert' | 'good' | 'info';
export type NoteIcon =
  | 'crisis'
  | 'shield'
  | 'war'
  | 'lock'
  | 'ban'
  | 'coin'
  | 'flag'
  | 'research'
  | 'build'
  | 'ship'
  | 'pirates'
  | 'market'
  | 'warn';

export interface Note {
  readonly tick: number;
  readonly kind: NoteKind;
  readonly icon: NoteIcon;
  readonly text: string;
  /** Where the camera flies on a click. */
  readonly hex?: Hex;
  /** Shown in the banner already: the log keeps it, the stack does not. */
  readonly bannerOnly?: boolean;
}

/** What a find gave (#101): «+10 металлы, +1 фрагмент технологии». */
export function gainsText(
  locale: Locale,
  gains: Readonly<Record<string, number | string>>,
): string {
  const parts: string[] = [];
  for (const g of TRADE_GOOD_IDS) {
    const n = gains[g];
    if (typeof n === 'number' && n > 0) {
      parts.push(`+${num(locale, n)} ${t(locale, `resource.${g}`).toLowerCase()}`);
    }
  }
  if (typeof gains.science === 'number') {
    parts.push(t(locale, 'note.gain.science', { n: num(locale, gains.science) }));
  }
  if (typeof gains.fragments === 'number') {
    parts.push(
      typeof gains.fragmentTech === 'string'
        ? t(locale, 'note.gain.fragmentsFor', {
            n: gains.fragments,
            tech: t(locale, `tech.${gains.fragmentTech as TechId}.name`),
          })
        : t(locale, 'note.gain.fragments', { n: gains.fragments }),
    );
  }
  if (gains.asteroid) parts.push(t(locale, 'note.gain.asteroid'));
  if (typeof gains.pirates === 'number') {
    parts.push(t(locale, 'note.gain.pirates', { level: gains.pirates }));
  }
  return parts.join(', ');
}

/** A technology's effects and what it opens, in one line. */
function techSummary(locale: Locale, tech: TechId): string {
  const unlocks = techUnlocks(locale, tech);
  return [
    ...techEffects(locale, tech),
    ...(unlocks.length ? [t(locale, 'note.unlocks', { list: unlocks.join(', ') })] : []),
  ].join('; ');
}

/** The numbers of the trade route's levels, for its texts. */
export const routePerks = () => ({
  cargo: Math.round(TRADE_AGREEMENT.cargoBonus * 100),
  loot: Math.round(TRADE_AGREEMENT.armoredLootShare * 100),
});

/**
 * `projects` remembers each project's kind from its opening: a resolved
 * project is already gone from the state.
 */
export function noteFor(
  event: GameEvent,
  state: GameState,
  v: Viewer,
  projects: ReadonlyMap<number, ProjectId> = new Map(),
): Note | null {
  const { me, locale } = v;
  const name = (id: string) =>
    id === PIRATES ? t(locale, 'find.pirates.name') : playerName(locale, v.setup, id);
  const note = (kind: NoteKind, icon: NoteIcon, text: string, extra: Partial<Note> = {}): Note => ({
    tick: state.tick,
    kind,
    icon,
    text,
    ...extra,
  });
  const capital = (id: string) => state.system.planets.find((p) => p.homeOf === id)?.hex;
  const player = state.players.find((p) => p.id === me);
  const outpost =
    event.type === 'outpostReady'
      ? state.outposts.find((o) => o.id === event.outpostId)
      : undefined;

  switch (event.type) {
    case 'crisisWarning':
      return event.playerId === me
        ? note(
            'heat',
            'crisis',
            t(locale, 'note.crisisWarning', {
              crisis: t(locale, `crisis.${event.kind}.name`),
              time: clock(event.strikeAt - state.tick),
            }),
            { bannerOnly: true },
          )
        : null;
    case 'crisisStruck':
      return note(
        'heat',
        'crisis',
        t(locale, 'note.crisisStruck', { crisis: t(locale, `crisis.${event.kind}.name`) }),
      );
    case 'crisisDamage':
      return event.playerId === me
        ? note(
            'alert',
            'crisis',
            t(locale, 'note.crisisDamage', {
              population: event.populationLost,
              outposts: event.outpostsDown,
              ships: event.shipsLost,
            }),
          )
        : null;
    case 'projectOpened':
      return note(
        'heat',
        'shield',
        t(locale, 'note.projectOpened', { project: t(locale, `project.${event.kind}.name`) }),
        {
          hex: event.hex,
          bannerOnly: true,
        },
      );
    case 'projectResolved': {
      const kind =
        state.projects.find((p) => p.id === event.projectId)?.kind ?? projects.get(event.projectId);
      const title = kind ? t(locale, `project.${kind}.name`) : '';
      return note(
        event.outcome === 'failed' ? 'alert' : 'heat',
        'shield',
        t(locale, `note.project.${event.outcome}`, { project: title }),
      );
    }
    case 'instabilityThreshold':
      return note('heat', 'crisis', t(locale, 'note.instability', { threshold: event.threshold }));
    case 'warDeclared':
      if (event.target === me) {
        return note(
          'alert',
          'war',
          t(locale, 'note.warOnYou', {
            name: name(event.by),
            time: clock(event.startsAt - state.tick),
          }),
          {
            ...(capital(event.by) ? { hex: capital(event.by)! } : {}),
          },
        );
      }
      return event.by === me
        ? null
        : note(
            'info',
            'war',
            t(locale, 'note.war', { by: name(event.by), target: name(event.target) }),
          );
    case 'proposed':
      // Someone offers the player peace or an agreement: the answer is in the Diplomacy tab.
      return event.b === me
        ? note(
            'info',
            'flag',
            t(locale, 'note.proposal', {
              name: name(event.a),
              kind: t(locale, `agreement.${event.kind}.name`),
            }),
          )
        : null;
    case 'agreed':
      // Peace has its own note; agreements the player signs get this one.
      return event.kind !== 'peace' && (event.a === me || event.b === me)
        ? note(
            'good',
            'flag',
            t(locale, 'note.agreed', {
              name: name(event.a === me ? event.b : event.a),
              kind: t(locale, `agreement.${event.kind}.name`),
            }),
          )
        : null;
    case 'routeLevelUp':
      return event.a === me || event.b === me
        ? note(
            'good',
            'coin',
            t(locale, 'note.routeLevel', {
              name: name(event.a === me ? event.b : event.a),
              level: event.level,
              perk: t(locale, `diplomacy.route.perk${event.level}`, routePerks()),
            }),
          )
        : null;
    case 'peaceMade':
      return event.a === me || event.b === me
        ? note(
            'good',
            'flag',
            t(locale, 'note.peace', { name: name(event.a === me ? event.b : event.a) }),
          )
        : null;
    case 'raidStarted':
      return event.victim === me
        ? note('alert', 'war', t(locale, 'note.raidOnYou', { name: name(event.playerId) }))
        : null;
    case 'raided':
      return event.victim === me
        ? note('alert', 'war', t(locale, 'note.raidedYou', { name: name(event.playerId) }))
        : null;
    case 'siegeStarted':
      return event.victim === me
        ? note('alert', 'war', t(locale, 'note.siegeOnYou', { name: name(event.playerId) }), {
            ...(capital(me) ? { hex: capital(me)! } : {}),
          })
        : null;
    case 'vassalized':
      return note(
        event.vassal === me ? 'alert' : 'info',
        'flag',
        t(locale, 'note.vassalized', {
          overlord: name(event.overlord),
          vassal: name(event.vassal),
        }),
      );
    case 'independence':
      return note('info', 'flag', t(locale, 'note.independence', { vassal: name(event.vassal) }));
    case 'sanctionsImposed':
      return event.target === me
        ? note(
            'alert',
            'lock',
            t(locale, 'note.sanctionsOnYou', { names: event.by.map(name).join(', ') }),
          )
        : null;
    case 'leverUsed':
      return event.target === me && event.lever === 'embargo'
        ? note('alert', 'ban', t(locale, 'note.embargoOnYou', { name: name(event.playerId) }))
        : null;
    case 'reserveDeclared':
      return note(
        event.playerId === me ? 'good' : 'info',
        'coin',
        t(locale, 'note.reserve', { name: name(event.playerId) }),
      );
    case 'reserveLost':
      return event.playerId === me ? note('alert', 'coin', t(locale, 'note.reserveLost')) : null;
    case 'piratesWarning':
      return event.playerId === me
        ? note('alert', 'pirates', t(locale, 'note.pirates'), { hex: event.hex })
        : null;
    case 'victoryCountdown':
      return note(
        event.playerId === me ? 'good' : 'alert',
        'flag',
        event.playerId === me
          ? t(locale, 'note.countdownYou', {
              victory: t(locale, `victory.${event.kind}.name`),
              time: clock(event.endsAt - state.tick),
            })
          : t(locale, 'note.countdownRival', {
              name: name(event.playerId),
              victory: t(locale, `victory.${event.kind}.name`),
              time: clock(event.endsAt - state.tick),
            }),
      );
    case 'countdownStopped':
      return note(
        'info',
        'flag',
        t(locale, 'note.countdownStopped', { name: name(event.playerId) }),
      );
    case 'countdownPaused':
    case 'countdownResumed': {
      // Bad news for whoever counts down is good news for the rest.
      const bad = (event.type === 'countdownPaused') === (event.playerId === me);
      return note(
        bad ? 'alert' : 'info',
        'flag',
        t(
          locale,
          event.type === 'countdownPaused' ? 'note.countdownPaused' : 'note.countdownResumed',
          {
            name: name(event.playerId),
            time: clock(event.endsAt - state.tick),
          },
        ),
      );
    }
    case 'megastructureStage':
      return event.playerId === me
        ? null
        : note(
            'alert',
            'flag',
            t(locale, 'note.swarm', {
              name: name(event.playerId),
              stages: event.stages,
              total: MEGASTRUCTURE.stages,
            }),
          );
    case 'researchCompleted':
      return event.playerId === me
        ? note(
            'good',
            'research',
            t(locale, 'note.research', {
              tech: t(locale, `tech.${event.tech}.name`),
              effects: techSummary(locale, event.tech),
            }).replace(/\.\s*$/, ''),
          )
        : null;
    case 'constructionCompleted':
      return event.playerId === me
        ? note(
            'good',
            'build',
            t(locale, 'note.built', {
              building: t(locale, `building.${event.building}.name`),
              level: event.level,
              does: player ? buildingDoes(locale, event.building, player) : '',
            }),
          )
        : null;
    case 'shipBuilt':
      return event.playerId === me
        ? note(
            'good',
            'ship',
            t(locale, 'note.ship', { ship: t(locale, `ship.${event.ship}.name`) }),
          )
        : null;
    case 'outpostReady':
      return event.playerId === me
        ? note(
            'good',
            'build',
            outpost
              ? t(locale, 'note.outpostYield', {
                  outpost: outpostName(locale, outpost),
                  n: num(locale, player ? outpostYield(player, outpost) : 0, 1),
                  good: t(locale, `resource.${outpost.good}`).toLowerCase(),
                })
              : t(locale, 'note.outpost', { outpost: t(locale, `outpost.${event.kind}.name`) }),
          )
        : null;
    case 'found':
      return event.playerId === me
        ? note(
            'good',
            'research',
            t(locale, 'note.found', {
              find: t(locale, `find.${event.find}.name`),
              gains: gainsText(locale, event.gains) || t(locale, 'note.gain.nothing'),
            }),
          )
        : null;
    case 'starvation':
      return event.playerId === me
        ? note(
            'alert',
            'warn',
            t(locale, 'note.starvation', { population: num(locale, event.population, 1) }),
          )
        : null;
    case 'upkeepUnpaid':
      return event.playerId === me
        ? note(
            'alert',
            'warn',
            t(locale, 'note.upkeep', { speed: num(locale, FLEET.unsuppliedSpeed, 1) }),
          )
        : null;
    case 'caravanRobbed': {
      const contract = state.contracts.find((c) => c.id === event.contractId);
      const mine = !contract || contract.seller === me || contract.buyer === me;
      return mine
        ? note('alert', 'pirates', t(locale, 'note.caravanRobbed', { name: name(event.by) }))
        : null;
    }
    case 'fleetDestroyed':
      return event.playerId === me
        ? note(
            'alert',
            'ship',
            t(locale, 'note.fleetLost', {
              name: event.lead ? shipName(locale, event.lead) : String(event.fleetId),
            }),
          )
        : null;
    case 'marketEvent':
      return event.phase === 'warning'
        ? note(
            'info',
            'market',
            t(locale, 'note.market', {
              event: t(locale, `market.${event.kind}.name`),
              good: t(locale, `resource.${event.good}`),
            }),
          )
        : null;
    case 'commandRejected':
      return event.command.playerId === me
        ? note(
            'alert',
            'warn',
            t(locale, 'note.rejected', { reason: reasonText(locale, event.reason) }),
          )
        : null;
    default:
      return null;
  }
}
